import type { MetabaseDashboard } from '@/features/dashboard/api/metabaseDashboard'

/**
 * View model for the cockpit dashboard, derived from the raw Metabase
 * payload (see `metabaseDashboard.ts`). This is the frontend's own shape,
 * not bristol's — camelCase, and only what the transform can actually
 * derive from the wire data.
 */

export type CockpitCell = string | number | null

export type CockpitColumn = {
  name: string
  displayName: string
  baseType: string
  semanticType: string | null
}

export type CockpitTrend = {
  column: string
  /** Time granularity of the trend (e.g. 'month'), not a currency/percent unit. */
  unit: string | null
  lastValue: number | null
  previousValue: number | null
  /** Metabase's fractional change, e.g. -0.0424 for -4.24%. */
  changeFraction: number | null
}

export type CockpitCard = {
  /** Unique; `cardId` repeats when the same question is reused across tabs. */
  dashcardId: number
  cardId: number
  name: string
  display: string
  columns: CockpitColumn[]
  rows: CockpitCell[][]
  rowCount: number
  trends: CockpitTrend[]
}

export type CockpitDashboard = {
  dashboardId: number
  instance: string
  name: string
  cards: CockpitCard[]
  lastUpdated: string | null
}

const toColumns = (
  cols: MetabaseDashboard['cards'][number]['query_result']['data']['cols'],
  metadataColumns: MetabaseDashboard['cards'][number]['query_result']['data']['results_metadata']['columns']
): CockpitColumn[] =>
  cols.map((col, index) => ({
    name: col.name,
    displayName: col.display_name,
    baseType: col.base_type,
    semanticType: metadataColumns[index]?.semantic_type ?? null,
  }))

const toTrends = (
  insights: MetabaseDashboard['cards'][number]['query_result']['data']['insights']
): CockpitTrend[] =>
  (insights ?? []).map(insight => ({
    column: insight.col,
    unit: insight.unit,
    lastValue: insight['last-value'],
    previousValue: insight['previous-value'],
    changeFraction: insight['last-change'],
  }))

const toCard = (card: MetabaseDashboard['cards'][number]): CockpitCard => ({
  dashcardId: card.dashcard_id,
  cardId: card.card_id,
  name: card.name,
  display: card.display,
  columns: toColumns(
    card.query_result.data.cols,
    card.query_result.data.results_metadata.columns
  ),
  rows: card.query_result.data.rows,
  rowCount: card.query_result.row_count,
  trends: toTrends(card.query_result.data.insights),
})

const toLastUpdated = (cards: MetabaseDashboard['cards']): string | null =>
  cards.reduce<string | null>((latest, card) => {
    const startedAt = card.query_result.started_at
    if (latest === null) return startedAt
    return new Date(startedAt) > new Date(latest) ? startedAt : latest
  }, null)

export const toCockpitDashboard = (raw: MetabaseDashboard): CockpitDashboard => ({
  dashboardId: raw.dashboard_id,
  instance: raw.instance,
  name: raw.structure.name,
  cards: raw.cards.map(toCard),
  lastUpdated: toLastUpdated(raw.cards),
})
